import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import {
  getAssets,
  createAsset,
  updateAsset,
  deleteAsset,
  CreateAssetData,
} from '../../services/assetService';
import { getFlocs } from '../../services/flocService';
import { Asset, AssetLifecycleStatus } from '@easydone/shared';
import {
  LocationFilterMap,
  filterItemsByLocation,
  formatDistance,
  LOCATION_RADIUS_OPTIONS,
  LocationData,
} from '../../components/LocationFilterMap';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  CubeIcon,
  QrCodeIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  MapIcon,
  Squares2X2Icon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

interface AssetWithLocation extends Asset {
  location?: LocationData;
  distance?: number;
  flocName?: string;
  flocCode?: string;
  flocCriticality?: string;
}

export function AssetsPageEnhanced() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const canManageAssets = usePermission('assets.manage');

  const [assets, setAssets] = useState<AssetWithLocation[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<AssetWithLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [showQRModal, setShowQRModal] = useState<Asset | null>(null);

  // View mode state
  const [viewMode, setViewMode] = useState<'list' | 'map' | 'grid'>('grid');

  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [locationRadius, setLocationRadius] = useState(LOCATION_RADIUS_OPTIONS[3].value); // Default 1km
  const [locationError, setLocationError] = useState('');
  const [locationWatchId, setLocationWatchId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [currentOrg]);

  useEffect(() => {
    filterAssets();
  }, [searchQuery, lifecycleFilter, assets, locationEnabled, currentLocation, locationRadius]);

  // Auto-enable location when switching to map view
  useEffect(() => {
    if (viewMode === 'map' && !locationEnabled && !currentLocation) {
      enableLocation();
    }
  }, [viewMode]);

  // Cleanup location watch on unmount
  useEffect(() => {
    return () => {
      if (locationWatchId !== null) {
        navigator.geolocation.clearWatch(locationWatchId);
      }
    };
  }, [locationWatchId]);

  const loadData = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [assetsData, flocsData] = await Promise.all([
        getAssets(currentOrg.id),
        getFlocs(currentOrg.id),
      ]);

      // Enrich assets with FLOC location data
      const enrichedAssets = assetsData.map(asset => {
        const floc = flocsData.find(f => f.id === asset.currentFlocId);
        return {
          ...asset,
          location: floc?.location,
          flocName: floc?.name,
          flocCode: floc?.code,
          flocCriticality: floc?.criticality,
        };
      });

      setAssets(enrichedAssets);
      setFilteredAssets(enrichedAssets);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const enableLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationError('');
    // Use watchPosition for continuous location updates
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationEnabled(true);
        setLocationError('');
      },
      (error) => {
        setLocationEnabled(false);
        setCurrentLocation(null);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('Unknown location error');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
    setLocationWatchId(watchId);
  };

  const disableLocation = () => {
    if (locationWatchId !== null) {
      navigator.geolocation.clearWatch(locationWatchId);
      setLocationWatchId(null);
    }
    setLocationEnabled(false);
    setCurrentLocation(null);
  };

  const filterAssets = () => {
    let filtered = assets;

    // Filter by lifecycle status
    if (lifecycleFilter !== 'all') {
      filtered = filtered.filter(asset => asset.lifecycleStatus === lifecycleFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        asset =>
          asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          asset.assetId.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (asset.serialNumber && asset.serialNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
          asset.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (asset.flocName && asset.flocName.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by location if enabled (for map view)
    if (viewMode === 'map' && locationEnabled && currentLocation) {
      filtered = filterItemsByLocation(filtered, currentLocation, locationRadius);
    }

    setFilteredAssets(filtered);
  };

  const handleCreateAsset = async (data: CreateAssetData) => {
    if (!currentOrg || !currentUser) return;

    try {
      await createAsset(currentOrg.id, data, currentUser.uid);
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to create asset:', error);
      alert('Failed to create asset');
    }
  };

  const handleUpdateAsset = async (assetId: string, data: Partial<CreateAssetData>) => {
    if (!currentOrg || !currentUser) return;

    try {
      await updateAsset(currentOrg.id, assetId, data, currentUser.uid);
      setEditingAsset(null);
      loadData();
    } catch (error) {
      console.error('Failed to update asset:', error);
      alert('Failed to update asset');
    }
  };

  const handleDeleteAsset = async (assetId: string) => {
    if (!currentOrg) return;
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      await deleteAsset(currentOrg.id, assetId);
      loadData();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset');
    }
  };

  const getLifecycleColor = (status: AssetLifecycleStatus) => {
    switch (status) {
      case 'commissioned': return 'bg-green-100 text-green-800';
      case 'in_storage': return 'bg-gray-100 text-gray-800';
      case 'in_maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'decommissioned': return 'bg-red-100 text-red-800';
      case 'disposed': return 'bg-slate-100 text-slate-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCriticalityColor = (criticality?: string) => {
    if (!criticality) return 'bg-gray-100 text-gray-800';
    switch (criticality) {
      case 'very_high': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      case 'very_low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Render marker popup for map view
  const renderMarkerPopup = (asset: AssetWithLocation) => (
    <div className="text-sm">
      <div className="font-semibold mb-1">{asset.name}</div>
      <div className="text-xs text-gray-600 mb-1">
        Tag: {asset.assetId}
      </div>
      <div className="text-xs text-gray-600 mb-1">
        Type: {asset.type}
      </div>
      {asset.flocName && (
        <div className="text-xs text-gray-600 mb-1">
          FLOC: {asset.flocName}
        </div>
      )}
      {asset.flocCriticality && (
        <div className="text-xs mb-1">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCriticalityColor(asset.flocCriticality)}`}>
            {asset.flocCriticality.toUpperCase()}
          </span>
        </div>
      )}
      {asset.conditionScore !== undefined && (
        <div className="text-xs text-gray-600 mb-1">
          Condition: {asset.conditionScore} ({asset.conditionGrade})
        </div>
      )}
      {asset.distance && (
        <div className="text-xs text-blue-600 mb-2">
          Distance: {formatDistance(asset.distance)}
        </div>
      )}
      <button
        onClick={() => navigate(`/assets/${asset.id}`)}
        className="text-xs text-blue-600 hover:underline"
      >
        View Details →
      </button>
    </div>
  );

  const renderAssetCard = (asset: AssetWithLocation) => (
    <div key={asset.id} className="card hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <Link
            to={`/assets/${asset.id}`}
            className="text-lg font-semibold text-gray-900 hover:text-primary-600"
          >
            {asset.name}
          </Link>
          <p className="text-sm text-gray-600 mt-1">
            SN: {asset.serialNumber || asset.assetId}
          </p>
          {asset.distance && (
            <p className="text-sm text-blue-600 font-medium mt-1">
              {formatDistance(asset.distance)} away
            </p>
          )}
        </div>
        {canManageAssets && (
          <div className="flex space-x-2">
            <button
              onClick={() => setShowQRModal(asset)}
              className="p-1 text-gray-400 hover:text-primary-600"
              title="View QR Code"
            >
              <QrCodeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setEditingAsset(asset)}
              className="p-1 text-gray-400 hover:text-primary-600"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteAsset(asset.id)}
              className="p-1 text-gray-400 hover:text-red-600"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Type:</span>
          <span className="font-medium text-gray-900">{asset.type}</span>
        </div>
        {asset.lifecycleStatus && (
          <div className="flex justify-between">
            <span className="text-gray-600">Status:</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getLifecycleColor(asset.lifecycleStatus)}`}>
              {asset.lifecycleStatus.replace('_', ' ')}
            </span>
          </div>
        )}
        {asset.flocName && (
          <div className="flex justify-between">
            <span className="text-gray-600">FLOC:</span>
            <span className="font-medium text-gray-900 truncate">{asset.flocName}</span>
          </div>
        )}
        {asset.flocCriticality && (
          <div className="flex justify-between">
            <span className="text-gray-600">Criticality:</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCriticalityColor(asset.flocCriticality)}`}>
              {asset.flocCriticality.toUpperCase()}
            </span>
          </div>
        )}
        {asset.conditionScore !== undefined && (
          <div className="flex justify-between">
            <span className="text-gray-600">Condition:</span>
            <span className={`text-sm font-bold ${
              asset.conditionScore >= 80 ? 'text-green-600' :
              asset.conditionScore >= 60 ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              {asset.conditionScore} ({asset.conditionGrade})
            </span>
          </div>
        )}
        {asset.category && (
          <div className="flex justify-between">
            <span className="text-gray-600">Category:</span>
            <span className="font-medium text-gray-900">{asset.category}</span>
          </div>
        )}
        {asset.manufacturer && (
          <div className="flex justify-between">
            <span className="text-gray-600">Manufacturer:</span>
            <span className="font-medium text-gray-900">{asset.manufacturer}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderAssetListRow = (asset: AssetWithLocation) => (
    <div
      key={asset.id}
      onClick={() => navigate(`/assets/${asset.id}`)}
      className="card hover:shadow-lg transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getLifecycleColor(asset.lifecycleStatus)}`}>
              {asset.lifecycleStatus.replace('_', ' ')}
            </span>
            <h3 className="text-lg font-semibold text-gray-900">
              {asset.name}
            </h3>
            {asset.distance && (
              <span className="text-sm text-blue-600 font-medium">
                {formatDistance(asset.distance)} away
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
            <div>
              <span className="font-medium">Tag:</span> {asset.assetId}
            </div>
            <div>
              <span className="font-medium">Serial:</span> {asset.serialNumber || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Type:</span> {asset.type}
            </div>
            {asset.flocName && (
              <div>
                <span className="font-medium">FLOC:</span> {asset.flocName}
              </div>
            )}
            {asset.flocCriticality && (
              <div>
                <span className="font-medium">Criticality:</span>{' '}
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCriticalityColor(asset.flocCriticality)}`}>
                  {asset.flocCriticality.toUpperCase()}
                </span>
              </div>
            )}
            {asset.conditionScore !== undefined && (
              <div>
                <span className="font-medium">Condition:</span>{' '}
                <span className={`font-bold ${
                  asset.conditionScore >= 80 ? 'text-green-600' :
                  asset.conditionScore >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {asset.conditionScore} ({asset.conditionGrade})
                </span>
              </div>
            )}
          </div>
        </div>

        {canManageAssets && (
          <div className="flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowQRModal(asset);
              }}
              className="p-1 text-gray-400 hover:text-primary-600"
              title="View QR Code"
            >
              <QrCodeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingAsset(asset);
              }}
              className="p-1 text-gray-400 hover:text-primary-600"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteAsset(asset.id);
              }}
              className="p-1 text-gray-400 hover:text-red-600"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading assets...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
          <p className="text-gray-600 mt-1">
            Manage your equipment and assets
          </p>
        </div>
        <div className="flex gap-2">
          {/* Map/List View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="List View"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Grid View"
            >
              <Squares2X2Icon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Map View"
            >
              <MapIcon className="w-5 h-5" />
            </button>
          </div>

          {canManageAssets && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Asset
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search assets by name, ID, serial number, type, or FLOC..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <select
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
          className="input w-full sm:w-64"
        >
          <option value="all">All Lifecycle Status</option>
          <option value="in_storage">In Storage</option>
          <option value="commissioned">Commissioned</option>
          <option value="in_maintenance">In Maintenance</option>
          <option value="decommissioned">Decommissioned</option>
          <option value="disposed">Disposed</option>
        </select>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <LocationFilterMap
          items={filteredAssets}
          locationEnabled={locationEnabled}
          currentLocation={currentLocation}
          locationRadius={locationRadius}
          locationError={locationError}
          onEnableLocation={enableLocation}
          onDisableLocation={disableLocation}
          onRadiusChange={setLocationRadius}
          renderMarkerPopup={renderMarkerPopup}
          onItemClick={(asset) => navigate(`/assets/${asset.id}`)}
        />
      )}

      {/* List/Grid View */}
      {viewMode !== 'map' && (
        <>
          {filteredAssets.length === 0 ? (
            <div className="card text-center py-12">
              <CubeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery || lifecycleFilter !== 'all' ? 'No assets found' : 'No assets yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || lifecycleFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first asset'}
              </p>
              {!searchQuery && lifecycleFilter === 'all' && canManageAssets && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary"
                >
                  Add Asset
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAssets.map(renderAssetCard)}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAssets.map(renderAssetListRow)}
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAsset) && (
        <AssetModal
          asset={editingAsset}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAsset(null);
          }}
          onSave={(data) => {
            if (editingAsset) {
              handleUpdateAsset(editingAsset.id, data);
            } else {
              handleCreateAsset(data);
            }
          }}
        />
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <QRCodeModal
          asset={showQRModal}
          onClose={() => setShowQRModal(null)}
        />
      )}
    </div>
  );
}

interface AssetModalProps {
  asset: Asset | null;
  onClose: () => void;
  onSave: (data: CreateAssetData) => void;
}

function AssetModal({ asset, onClose, onSave }: AssetModalProps) {
  const [formData, setFormData] = useState<CreateAssetData>({
    name: asset?.name || '',
    assetId: asset?.assetId || '',
    serialNumber: asset?.serialNumber || '',
    type: asset?.type || '',
    category: asset?.category || '',
    storageLocation: asset?.storageLocation || '',
    initialConditionScore: asset?.conditionScore || 100,
    manufacturer: asset?.manufacturer || '',
    model: asset?.model || '',
    description: asset?.description || '',
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {asset ? 'Edit Asset' : 'Create New Asset'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset ID/Code *
                </label>
                <input
                  type="text"
                  value={formData.assetId}
                  onChange={(e) => setFormData({ ...formData, assetId: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serial Number *
              </label>
              <input
                type="text"
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                className="input w-full"
                placeholder="Physical serial number"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Pump, Valve, Meter"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Mechanical, Electrical"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Storage Location
                </label>
                <input
                  type="text"
                  value={formData.storageLocation}
                  onChange={(e) => setFormData({ ...formData, storageLocation: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Warehouse A, Shelf 3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Where asset is stored before commissioning
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Condition Score
                </label>
                <input
                  type="number"
                  value={formData.initialConditionScore}
                  onChange={(e) => setFormData({ ...formData, initialConditionScore: Number(e.target.value) })}
                  className="input w-full"
                  min="0"
                  max="100"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  0-100 scale (default: 100 for new assets)
                </p>
              </div>
            </div>

            {/* Equipment Details */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900 mb-3">Equipment Details</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Manufacturer
                    </label>
                    <input
                      type="text"
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model
                    </label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input w-full"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : asset ? 'Update Asset' : 'Create Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface QRCodeModalProps {
  asset: Asset;
  onClose: () => void;
}

function QRCodeModal({ asset, onClose }: QRCodeModalProps) {
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateQR();
  }, []);

  const generateQR = async () => {
    try {
      const { generateAssetQRCode } = await import('../../services/qrCodeService');
      const url = await generateAssetQRCode(asset.orgId, asset.id, asset.name);
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrCodeUrl) return;
    const { downloadQRCode } = require('../../services/qrCodeService');
    downloadQRCode(qrCodeUrl, asset.name);
  };

  const handlePrint = () => {
    if (!qrCodeUrl) return;
    const { printQRCodeLabel } = require('../../services/qrCodeService');
    printQRCodeLabel(qrCodeUrl, asset.name, `Asset ID: ${asset.assetId}`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">QR Code - {asset.name}</h2>

        <div className="flex flex-col items-center mb-6">
          {loading ? (
            <div className="w-64 h-64 flex items-center justify-center bg-gray-100 border-2 border-gray-200 rounded">
              <p className="text-gray-500">Generating QR Code...</p>
            </div>
          ) : qrCodeUrl ? (
            <img
              src={qrCodeUrl}
              alt="QR Code"
              className="w-64 h-64 border-2 border-gray-200 rounded"
            />
          ) : (
            <div className="w-64 h-64 flex items-center justify-center bg-gray-100 border-2 border-gray-200 rounded">
              <p className="text-gray-500">Failed to generate QR Code</p>
            </div>
          )}
          <p className="text-sm text-gray-600 mt-4 text-center">
            Asset ID: {asset.assetId}
          </p>
        </div>

        {qrCodeUrl && (
          <div className="flex space-x-3 mb-4">
            <button
              onClick={handleDownload}
              className="flex-1 btn btn-secondary flex items-center justify-center"
            >
              <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
              Download
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 btn btn-secondary flex items-center justify-center"
            >
              <PrinterIcon className="w-5 h-5 mr-2" />
              Print
            </button>
          </div>
        )}

        <button onClick={onClose} className="btn btn-primary w-full">
          Close
        </button>
      </div>
    </div>
  );
}
